/**
 * LayeredAnswer — a finished chat answer shown as a bulleted list of
 * one-line headlines (bullets, not numbers, so the numbered lists inside an
 * opened point are not confused with the points), each with its own "Read more" (docs/specs/lookup.md).
 *
 * The headlines are the only new text. "Read more" shows the part of the
 * raw answer the headline stands for, exactly as written:
 * `content.slice(start, end)`, rendered like any other answer. Nothing is
 * open at first, and a closed part is not in the page at all, so the
 * References block (the last one) stays out of sight until it is opened.
 *
 * A layout that does not fit the answer (no blocks, offsets out of range,
 * out of order or overlapping, or text left outside every block) is not
 * trusted: the whole answer is shown as written instead, so nothing in it
 * can be hidden or lost.
 *
 * For analytics, each open block counts the time it is open while the page
 * is visible, and its close reports the whole seconds. Leaving with blocks
 * still open (the tab closed or reloaded, or the chat left) closes each of
 * them, once, with `left`. Each block's item carries `data-answer-block`,
 * so a link clicked in its headline or text names its block.
 *
 * Above the list, "Expand all" opens every block and, once all are open,
 * "Collapse all" closes them. A press is reported once, as itself, with the
 * blocks it opened or closed (`onToggleAll`), never as one Read more per
 * block. Blocks it opened are timed like any other, and a single block can
 * still be opened or closed after it.
 */

import React, { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import ChatMessageMarkdown from './ChatMessageMarkdown';
import LinkedHeadline from './LinkedHeadline';
import type { ExpandAllBlock, LayeredAnswerLayout, ReadMoreTiming } from '../../lookupApi';

interface LayeredAnswerProps {
    /** The raw answer, unchanged. */
    content: string;
    layered: LayeredAnswerLayout;
    /** Called on each open (`open` true) and close of a block, by index. A
     *  close carries its `timing`: one close per open, so a block already
     *  closed by leaving the page (`pagehide`) reports no second close. */
    onToggle?: (block: number, open: boolean, timing?: ReadMoreTiming) => void;
    /** Called once per press of "Expand all" (`open` true) or "Collapse all",
     *  with the blocks it opened or closed, in order. A closed block carries
     *  its `seconds`, as a close through `onToggle` does. */
    onToggleAll?: (open: boolean, blocks: ExpandAllBlock[]) => void;
}

/** An open block's time so far: `ms` visible before `since`, the start of
 *  the current visible stretch (null while the page is hidden). */
interface OpenTime {
    ms: number;
    since: number | null;
}

const isVisible = () => document.visibilityState === 'visible';
const secondsOpen = (time: OpenTime, now: number) => Math.round((time.ms + (time.since === null ? 0 : now - time.since)) / 1000);

/** True when every block is a real, in-order slice of `content` and the text
 *  between blocks is only whitespace, so the layers hold the whole answer. */
export function isUsableLayout(content: string, layered: LayeredAnswerLayout | null | undefined): boolean {
    const blocks = layered?.blocks;
    if (!Array.isArray(blocks) || blocks.length === 0) return false;
    const { references } = layered as LayeredAnswerLayout;
    if (references != null && !(Number.isInteger(references) && references >= 0 && references < blocks.length)) return false;
    let covered = 0;
    for (const block of blocks) {
        if (!block || typeof block.headline !== 'string' || !block.headline.trim()) return false;
        const { start, end } = block;
        if (!Number.isInteger(start) || !Number.isInteger(end)) return false;
        if (start < 0 || end > content.length || start >= end || start < covered) return false;
        if (content.slice(covered, start).trim()) return false;
        covered = end;
    }
    return !content.slice(covered).trim();
}

function LayeredAnswer({ content, layered, onToggle, onToggleAll }: LayeredAnswerProps) {
    const id = useId();
    const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set());
    const times = useRef(new Map<number, OpenTime>());
    const report = useRef(onToggle);
    report.current = onToggle;

    useEffect(() => {
        const opened = times.current;
        const visibility = () => {
            const now = Date.now();
            opened.forEach(time => {
                if (isVisible()) {
                    if (time.since === null) time.since = now;
                } else if (time.since !== null) {
                    time.ms += now - time.since;
                    time.since = null;
                }
            });
        };
        const leave = () => {
            const now = Date.now();
            opened.forEach((time, block) => report.current?.(block, false, { seconds: secondsOpen(time, now), left: true }));
            opened.clear();
        };
        document.addEventListener('visibilitychange', visibility);
        window.addEventListener('pagehide', leave);
        return () => {
            document.removeEventListener('visibilitychange', visibility);
            window.removeEventListener('pagehide', leave);
            leave();
        };
    }, []);

    if (!isUsableLayout(content, layered)) return <ChatMessageMarkdown content={content} />;

    const toggle = (index: number) => {
        const opening = !open.has(index);
        setOpen(now => {
            const next = new Set(now);
            if (opening) next.add(index); else next.delete(index);
            return next;
        });
        const now = Date.now();
        if (opening) {
            times.current.set(index, { ms: 0, since: isVisible() ? now : null });
            onToggle?.(index, true);
            return;
        }
        const time = times.current.get(index);
        times.current.delete(index);
        if (time) onToggle?.(index, false, { seconds: secondsOpen(time, now) });
    };

    const indexes = layered.blocks.map((_, index) => index);
    const allOpen = open.size === indexes.length;
    // One press, one report. A block already closed by leaving the page
    // (`pagehide`) closes here without being reported again.
    const toggleAll = () => {
        const now = Date.now();
        if (allOpen) {
            const closed: ExpandAllBlock[] = [];
            indexes.forEach(index => {
                const time = times.current.get(index);
                times.current.delete(index);
                if (time) closed.push({ block: index, seconds: secondsOpen(time, now) });
            });
            setOpen(new Set());
            onToggleAll?.(false, closed);
            return;
        }
        const opening = indexes.filter(index => !open.has(index));
        opening.forEach(index => times.current.set(index, { ms: 0, since: isVisible() ? now : null }));
        setOpen(new Set(indexes));
        onToggleAll?.(true, opening.map(block => ({ block })));
    };

    return (
        <div>
            {indexes.length > 1 && (
                <div className="flex justify-end mb-1">
                    <button
                        type="button"
                        aria-expanded={allOpen}
                        aria-controls={indexes.map(index => `${id}-body-${index}`).join(' ')}
                        onClick={toggleAll}
                        className="chat-read-more shrink-0 flex items-center gap-1 px-2 py-1 rounded-md hover:text-fg hover:bg-surface-sunken focus-visible:shadow-bark-focus"
                    >
                        {allOpen
                            ? <ChevronsDownUp size={14} strokeWidth={1.5} aria-hidden="true" />
                            : <ChevronsUpDown size={14} strokeWidth={1.5} aria-hidden="true" />}
                        {allOpen ? 'Collapse all' : 'Expand all'}
                    </button>
                </div>
            )}
            <ul className="chat-points list-disc list-outside pl-5 text-fg">
                {layered.blocks.map((block, index) => {
                    const expanded = open.has(index);
                    const headlineId = `${id}-headline-${index}`;
                    const bodyId = `${id}-body-${index}`;
                    return (
                        <li key={index} data-answer-block={index}>
                            {/* The whole row toggles; the button is the keyboard and
                                screen-reader control, so its click is not counted twice. */}
                            <div className="flex items-start gap-3 cursor-pointer" onClick={() => toggle(index)}>
                                <span id={headlineId} className="flex-1 min-w-0 py-0.5">
                                    <LinkedHeadline headline={block.headline} body={content.slice(block.start, block.end)} />
                                </span>
                                <button
                                    type="button"
                                    aria-expanded={expanded}
                                    aria-controls={bodyId}
                                    aria-describedby={headlineId}
                                    onClick={event => { event.stopPropagation(); toggle(index); }}
                                    className="chat-read-more shrink-0 flex items-center gap-1 px-2 py-1 rounded-md hover:text-fg hover:bg-surface-sunken focus-visible:shadow-bark-focus"
                                >
                                    {/* One down chevron; .chat-read-more turns it when open. */}
                                    <ChevronDown size={14} strokeWidth={1.5} aria-hidden="true" />
                                    {expanded ? 'Show less' : 'Read more'}
                                </button>
                            </div>
                            <div id={bodyId}>
                                {expanded && (
                                    <div className="chat-point-body">
                                        <ChatMessageMarkdown content={content.slice(block.start, block.end)} />
                                    </div>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default LayeredAnswer;
